import React, { useState, useCallback } from "react";
import PropTypes from "prop-types";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InlineMessage from "../../../common/InlineMessage/InlineMessage";
import FileUploader from "../../../common/FileUploader/FileUploader";
import { TARGET_DEVICE_OPTIONS } from "../adManagementFormConfig";
import FieldLabel from "./common/FieldLabel";
import {
  existingRecordPropType,
  formikPropType,
} from "./common/adManagementPropTypes";
import ImageCropperModal from "./ImageCropperModal/ImageCropperModal";
import BannerPositionField from "./BannerPositionField";
import { formatDate } from "../../../utilities/formatter";

/** Crop config keyed by banner position and target device. */
const CROP_CONFIG = {
  Top: {
    mobile: { width: 1050, height: 540, aspect: 1050 / 540 },
    tablet: { width: 1456, height: 640, aspect: 1456 / 640 },
    windows: { width: 1792, height: 512, aspect: 1792 / 512 },
  },
  Featured: {
    mobile: { width: 660, height: 360, aspect: 660 / 360 },
    tablet: { width: 600, height: 240, aspect: 600 / 240 },
    windows: { width: 1920, height: 1080, aspect: 1920 / 1080 },
  },
};
const DEFAULT_CROP_CONFIG = CROP_CONFIG.Featured.windows;

const getCropConfig = (position, device) =>
  CROP_CONFIG[position]?.[device] ?? DEFAULT_CROP_CONFIG;

const disabledFieldSx = {
  "& .MuiInputBase-root": { backgroundColor: "#f5f5f5" },
};

const DeviceUploadField = ({
  device,
  deviceOpt,
  isViewMode,
  devicePreviewUrls,
  deviceFiles,
  onSelectFile,
  onDeleteFile,
  position,
  error,
}) => {
  const [cropOpen, setCropOpen] = useState(false);
  const [rawSrc, setRawSrc] = useState(null);
  const [rawMeta, setRawMeta] = useState({
    name: "banner.jpg",
    type: "image/jpeg",
  });
  const fileInputRef = React.useRef(null);

  const handleRawFileInput = useCallback((event) => {
    const file = event.target?.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      setRawSrc(reader.result);
      setRawMeta({ name: file.name, type: file.type || "image/jpeg" });
      setCropOpen(true);
    };
    reader.readAsDataURL(file);
  }, []);

  const handleCropConfirm = useCallback(
    (croppedFile) => {
      setCropOpen(false);
      setRawSrc(null);
      /* Reset file input so the same image can be selected again */
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      onSelectFile(croppedFile);
    },
    [onSelectFile, fileInputRef],
  );

  const handleCropCancel = useCallback(() => {
    setCropOpen(false);
    setRawSrc(null);
    /* Reset file input so the same image can be selected again */
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, [fileInputRef]);

  const cropConfig = getCropConfig(position, device);
  const previewUrl = devicePreviewUrls[device];

  if (isViewMode) {
    if (previewUrl) {
      return (
        <img
          src={previewUrl}
          alt={`Banner Preview — ${deviceOpt?.label}`}
          style={{
            maxWidth: 400,
            maxHeight: 160,
            objectFit: "contain",
            border: "1px solid #ddd",
            borderRadius: 4,
          }}
        />
      );
    }
    return (
      <Typography variant="body2" color="text.secondary">
        No image uploaded
      </Typography>
    );
  }

  return (
    <Box>
      <ImageCropperModal
        open={cropOpen}
        imageSrc={rawSrc}
        aspectRatio={cropConfig.aspect}
        targetWidth={cropConfig.width}
        targetHeight={cropConfig.height}
        fileName={rawMeta.name}
        mimeType={rawMeta.type}
        onCancel={handleCropCancel}
        onConfirm={handleCropConfirm}
      />
      <FileUploader
        accept="image/png, image/jpeg"
        onSelectFile={handleRawFileInput}
        sourceFile={deviceFiles[device] || null}
        onDeleteFile={onDeleteFile}
        maxSizeInBytes={5 * 1024 * 1024}
        maxSizeExeedError="Banner image must be under 5 MB"
        disabled={false}
        deleteDisable={false}
        selectLabel={
          previewUrl && !deviceFiles[device] ? "Update File" : "Select File"
        }
        ref={fileInputRef}
      />
      {previewUrl && (
        <Box mt={1}>
          <img
            src={previewUrl}
            alt={`Banner Preview — ${deviceOpt?.label}`}
            style={{
              maxWidth: 300,
              maxHeight: 120,
              objectFit: "contain",
              border: "1px solid #ddd",
              borderRadius: 4,
            }}
          />
        </Box>
      )}
      <Typography variant="caption" color="text.secondary">
        .jpg or .png · max 5MB · recommended {cropConfig.width}×
        {cropConfig.height}px
      </Typography>
      <InlineMessage error={error || ""} />
    </Box>
  );
};

const BannerNameField = ({ formik, isViewMode }) => (
  <Grid item xs={12} md={6}>
    <FieldLabel required>Banner Name</FieldLabel>
    <TextField
      fullWidth
      size="small"
      name="bannerName"
      placeholder="Enter banner name"
      value={formik.values.bannerName}
      onChange={formik.handleChange}
      onBlur={formik.handleBlur}
      disabled={isViewMode}
      inputProps={{ maxLength: 50 }}
    />
    <InlineMessage
      error={
        formik.touched.bannerName && formik.errors.bannerName
          ? formik.errors.bannerName
          : ""
      }
    />
  </Grid>
);

const TargetDevicesField = ({
  formik,
  isViewMode,
  isLocked,
  onDeviceToggle,
  lockedDevices,
  isPositionSelected,
}) => (
  <Grid item xs={12}>
    <FieldLabel required>Target Devices</FieldLabel>
    <FormGroup row sx={{ paddingLeft: 1.8 }}>
      {TARGET_DEVICE_OPTIONS.map((device) => {
        const isChecked = formik.values.targetDevices.includes(device.value);
        /* Lock devices that were already saved on a purchased ad: they
         * cannot be unchecked, but new (currently-unchecked) devices stay
         * toggleable so the user can add coverage. */
        const isLockedChecked =
          isChecked && lockedDevices.includes(device.value);
        return (
          <FormControlLabel
            key={device.value}
            control={
              <Checkbox
                size="small"
                checked={isChecked}
                onChange={(event) =>
                  onDeviceToggle(device.value, event.target.checked)
                }
                disabled={
                  isViewMode || isLocked || !isPositionSelected || isLockedChecked
                }
              />
            }
            label={device.label}
          />
        );
      })}
    </FormGroup>
    <InlineMessage
      error={
        formik.touched.targetDevices && formik.errors.targetDevices
          ? formik.errors.targetDevices
          : ""
      }
    />
    {!isViewMode && (
      <Typography variant="caption" sx={{ color: "#1565c0" }}>
        {isPositionSelected
          ? "Select all devices this ad should target."
          : "Select a banner position before choosing target devices."}
      </Typography>
    )}
  </Grid>
);

const DeviceUploadFields = ({
  formik,
  isViewMode,
  devicePreviewUrls,
  deviceFiles,
  onDeviceFileSelect,
  onDeviceFileDelete,
  position,
}) =>
  formik.values.targetDevices.map((device) => {
    const deviceOpt = TARGET_DEVICE_OPTIONS.find(
      (option) => option.value === device,
    );
    /* Show device-image error only after the user has attempted to submit
     * (avoids errors flashing on initial render thanks to validateOnMount). */
    const showErrors = formik.submitCount > 0;
    const deviceError = showErrors ? formik.errors?.deviceFiles?.[device] : "";
    return (
      <Grid item xs={12} key={`${device}-${position || "no-position"}`}>
        <FieldLabel required>Upload file for {deviceOpt?.label}</FieldLabel>
        <DeviceUploadField
          device={device}
          deviceOpt={deviceOpt}
          isViewMode={isViewMode}
          devicePreviewUrls={devicePreviewUrls}
          deviceFiles={deviceFiles}
          onSelectFile={onDeviceFileSelect(device)}
          onDeleteFile={onDeviceFileDelete(device)}
          position={position}
          error={deviceError}
        />
      </Grid>
    );
  });

const AuditFields = ({ existingRecord }) => (
  <>
    <Grid item xs={12} sm={6} md={3}>
      <FieldLabel>Created Date</FieldLabel>
      <TextField
        fullWidth
        size="small"
        value={formatDate(existingRecord?.createdOn) ?? ""}
        disabled
        placeholder="—"
        sx={disabledFieldSx}
      />
    </Grid>
    <Grid item xs={12} sm={6} md={3}>
      <FieldLabel>Created By</FieldLabel>
      <TextField
        fullWidth
        size="small"
        value={existingRecord?.createdByName ?? ""}
        disabled
        placeholder="—"
        sx={disabledFieldSx}
      />
    </Grid>
    <Grid item xs={12} sm={6} md={3}>
      <FieldLabel>Last Updated Date</FieldLabel>
      <TextField
        fullWidth
        size="small"
        value={formatDate(existingRecord?.updatedOn) ?? ""}
        disabled
        placeholder="—"
        sx={disabledFieldSx}
      />
    </Grid>
    <Grid item xs={12} sm={6} md={3}>
      <FieldLabel>Last Updated By</FieldLabel>
      <TextField
        fullWidth
        size="small"
        value={existingRecord?.updatedByName ?? ""}
        disabled
        placeholder="—"
        sx={disabledFieldSx}
      />
    </Grid>
  </>
);

const BasicInformationSection = ({
  accordionSx,
  formik,
  isViewMode,
  isLimited,
  isEditMode,
  existingRecord,
  paymentLabel,
  deviceFiles,
  devicePreviewUrls,
  onDeviceToggle,
  onDeviceFileSelect,
  onDeviceFileDelete,
  lockedDevices,
  areTargetDevicesLocked,
  onPositionChange,
}) => (
  <Accordion defaultExpanded sx={accordionSx} className="app-accordion">
    <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ border: "none" }}>
      <Typography fontWeight={600}>Basic Information</Typography>
    </AccordionSummary>
    <AccordionDetails sx={{ pt: 2, backgroundColor: "#f8fcff" }}>
      <Grid container spacing={3}>
        <BannerNameField formik={formik} isViewMode={isViewMode} />
        <BannerPositionField
          formik={formik}
          isViewMode={isViewMode}
          isLimited={isLimited}
          onPositionChange={onPositionChange}
        />
        <TargetDevicesField
          formik={formik}
          isViewMode={isViewMode}
          isLocked={areTargetDevicesLocked}
          onDeviceToggle={onDeviceToggle}
          lockedDevices={lockedDevices}
          isPositionSelected={Boolean(formik.values.position)}
        />
        <DeviceUploadFields
          formik={formik}
          isViewMode={isViewMode}
          devicePreviewUrls={devicePreviewUrls}
          deviceFiles={deviceFiles}
          onDeviceFileSelect={onDeviceFileSelect}
          onDeviceFileDelete={onDeviceFileDelete}
          position={formik.values.position}
        />
        <AuditFields existingRecord={existingRecord} />
      </Grid>
    </AccordionDetails>
  </Accordion>
);

DeviceUploadField.propTypes = {
  device: PropTypes.string.isRequired,
  deviceOpt: PropTypes.shape({
    label: PropTypes.string,
    recommendedSize: PropTypes.string,
  }),
  isViewMode: PropTypes.bool.isRequired,
  devicePreviewUrls: PropTypes.object.isRequired,
  deviceFiles: PropTypes.object.isRequired,
  onSelectFile: PropTypes.func.isRequired,
  onDeleteFile: PropTypes.func.isRequired,
  position: PropTypes.string,
  error: PropTypes.string,
};
DeviceUploadField.defaultProps = {
  deviceOpt: null,
  position: "Featured",
  error: "",
};

BannerNameField.propTypes = {
  formik: formikPropType,
  isViewMode: PropTypes.bool.isRequired,
};

TargetDevicesField.propTypes = {
  formik: formikPropType,
  isViewMode: PropTypes.bool.isRequired,
  isLocked: PropTypes.bool,
  onDeviceToggle: PropTypes.func.isRequired,
  lockedDevices: PropTypes.arrayOf(PropTypes.string),
  isPositionSelected: PropTypes.bool.isRequired,
};
TargetDevicesField.defaultProps = {
  isLocked: false,
  lockedDevices: [],
};

DeviceUploadFields.propTypes = {
  formik: formikPropType,
  isViewMode: PropTypes.bool.isRequired,
  devicePreviewUrls: PropTypes.object.isRequired,
  deviceFiles: PropTypes.object.isRequired,
  onDeviceFileSelect: PropTypes.func.isRequired,
  onDeviceFileDelete: PropTypes.func.isRequired,
  position: PropTypes.string,
};

AuditFields.propTypes = {
  existingRecord: existingRecordPropType,
};
AuditFields.defaultProps = {
  existingRecord: null,
};

BasicInformationSection.propTypes = {
  accordionSx: PropTypes.object.isRequired,
  formik: formikPropType,
  isViewMode: PropTypes.bool.isRequired,
  isLimited: PropTypes.bool.isRequired,
  isEditMode: PropTypes.bool.isRequired,
  existingRecord: existingRecordPropType,
  paymentLabel: PropTypes.string.isRequired,
  deviceFiles: PropTypes.object.isRequired,
  devicePreviewUrls: PropTypes.object.isRequired,
  onDeviceToggle: PropTypes.func.isRequired,
  onDeviceFileSelect: PropTypes.func.isRequired,
  onDeviceFileDelete: PropTypes.func.isRequired,
  lockedDevices: PropTypes.arrayOf(PropTypes.string),
  areTargetDevicesLocked: PropTypes.bool,
  onPositionChange: PropTypes.func.isRequired,
};
BasicInformationSection.defaultProps = {
  existingRecord: null,
  lockedDevices: [],
  areTargetDevicesLocked: false,
};

export default BasicInformationSection;
